import { useMemo } from 'react'
import Add from '@mui/icons-material/Add'
import { Button, Skeleton, Stack } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { useDeleteVendorExclusion } from '@/service-library/hooks/vendor-exclusions'
import { Organization } from '@/types/organizations'
import { Project } from '@/types/projects'
import { VendorExclusion } from '@/types/rollup'
import { showErrorSnackbar } from '@/utils/snackbars'
import SettingsSection from '@/components/shared/settings/SettingsSection'
import CompanyExclusionsCard, {
  CompanyGroup,
  vendorExclusionLabel,
} from './CompanyExclusionsCard'
import ExcludeVendorsDialog from './ExcludeVendorsDialog'
import RollupEmptyState from './RollupEmptyState'

type VendorExclusionsTabProps = {
  project: Project
  dataProviderOrgs: Organization[]
  vendorExclusions: VendorExclusion[]
  vendorExclusionsQueryKey: QueryKey
  isPending: boolean
}

export default function VendorExclusionsTab({
  project,
  dataProviderOrgs,
  vendorExclusions,
  vendorExclusionsQueryKey,
  isPending,
}: VendorExclusionsTabProps) {
  const excludeOverlay = useOverlay()

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    dataProviderOrgs.forEach((org) => map.set(org.id, org.name))
    return map
  }, [dataProviderOrgs])

  // Grouped by company, then sorted entirely from the rows' own contents —
  // companies A–Z (matching the Configs tab) and vendors by their displayed
  // label. Deliberately *not* the order the list arrives in: several
  // exclusions created together get non-monotonic ULID ids minted in the same
  // millisecond, so the optimistic write shows them in the order they were
  // picked while the refetch returns them ordered by id. Sorting on the rows
  // themselves makes both orders identical, so nothing moves when the request
  // completes. It also beats creation order for scanning a long list.
  const companyGroups = useMemo<CompanyGroup[]>(() => {
    const groupsByOrgId = new Map<string, CompanyGroup>()
    vendorExclusions.forEach((exclusion) => {
      let group = groupsByOrgId.get(exclusion.dp_org_id)
      if (!group) {
        group = {
          dpOrgId: exclusion.dp_org_id,
          companyName:
            companyNameById.get(exclusion.dp_org_id) || exclusion.dp_org_id,
          exclusions: [],
        }
        groupsByOrgId.set(exclusion.dp_org_id, group)
      }
      group.exclusions.push(exclusion)
    })
    const groups = Array.from(groupsByOrgId.values())
    groups.forEach((group) => {
      group.exclusions.sort((a, b) =>
        // numeric, because a blank display_name falls back to vendor_num and
        // those are numbers — '9' should not sort after '10'.
        vendorExclusionLabel(a).localeCompare(
          vendorExclusionLabel(b),
          undefined,
          { numeric: true },
        ),
      )
    })
    return groups.sort((a, b) => a.companyName.localeCompare(b.companyName))
  }, [companyNameById, vendorExclusions])

  const { deleteVendorExclusion } = useDeleteVendorExclusion({
    listQueryKey: vendorExclusionsQueryKey,
    onError: () => {
      showErrorSnackbar(
        'Unable to remove vendor exclusion. Please try again later.',
      )
    },
  })

  return (
    <>
      <SettingsSection
        title="Excluded vendors"
        description="Rollup configs are never applied to excluded vendors, so their line items are never rolled up on export."
        actions={
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={excludeOverlay.open}
          >
            Exclude vendors
          </Button>
        }
        variant="plain"
      >
        {isPending ? (
          <Skeleton variant="rounded" height={56} />
        ) : companyGroups.length === 0 ? (
          <RollupEmptyState>
            No vendor exclusions. All vendors follow their matching rollup
            config.
          </RollupEmptyState>
        ) : (
          <Stack spacing={2}>
            {companyGroups.map((group) => (
              <CompanyExclusionsCard
                key={group.dpOrgId}
                group={group}
                onUnexclude={deleteVendorExclusion}
              />
            ))}
          </Stack>
        )}
      </SettingsSection>

      <ExcludeVendorsDialog
        overlay={excludeOverlay}
        project={project}
        dataProviderOrgs={dataProviderOrgs}
        vendorExclusions={vendorExclusions}
        vendorExclusionsQueryKey={vendorExclusionsQueryKey}
      />
    </>
  )
}
